import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

const CurrentLocationIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3.2" fill="#F07639" stroke="#F07639" />
    <circle cx="12" cy="12" r="7.2" />
    <line x1="12" y1="2" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="2" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
  </svg>
);

const SearchSection = ({
  onSearch,
  isSearched,
  isLoading,
  lastQuery = "",
  activeView = "overview",
  onViewChange,
  onPrefetchQuery,
  onRequireAuth,
}) => {
  const toTitleCase = (value = "") =>
    value
      .replace(/\uFFFD/g, "")
      .split(/\s+/)
      .map((word) => {
        if (!word) return "";
        const cleaned = word.replace(/[^a-zA-Z]/g, "");
        if (cleaned.length === 2) return word.toUpperCase();
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ")
      .replace(/\s{2,}/g, " ")
      .trim();

  const displayQuery = toTitleCase(lastQuery);

  const [address, setAddress] = useState(lastQuery);
  const [locating, setLocating] = useState(false);
  const navigate = useNavigate();
  const curatedView = activeView !== "overview";
  const { isSignedIn } = useAuth();
  const opencageKey = process.env.REACT_APP_OPENCAGE_KEY;
  const guestSearchKey = "guestLocationSearches";
  const GUEST_SEARCH_LIMIT = 3;
  // TEMP: disable guest search limit / login prompt during testing. Set false to re-enable.
  const DISABLE_GUEST_SEARCH_LIMIT = true;
  const GUEST_SEARCH_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
  const autoLocationPromptedRef = useRef(false);

  useEffect(() => {
    setAddress(lastQuery);
  }, [lastQuery]);

  useEffect(() => {
    if (!curatedView && onPrefetchQuery) {
      const trimmed = address.trim();
      if (trimmed.length >= 2) {
        onPrefetchQuery(trimmed);
      }
    }
  }, [address, curatedView, onPrefetchQuery]);

  const promptSignUp = useCallback(
    () => {
      if (onRequireAuth) {
        onRequireAuth();
        return;
      }
      navigate("/login");
    },
    [navigate, onRequireAuth]
  );

  const getGuestSearchMeta = useCallback(() => {
    if (isSignedIn) {
      return { count: 0, windowStart: Date.now() };
    }

    if (typeof window === "undefined") {
      return { count: 0, windowStart: Date.now() };
    }

    try {
      const raw = window.localStorage.getItem(guestSearchKey);
      if (!raw) {
        return { count: 0, windowStart: Date.now() };
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        return { count: 0, windowStart: Date.now() };
      }

      const now = Date.now();
      const windowStart =
        typeof parsed.windowStart === "number" ? parsed.windowStart : now;
      const count = typeof parsed.count === "number" ? parsed.count : 0;

      if (now - windowStart > GUEST_SEARCH_WINDOW_MS) {
        return { count: 0, windowStart: now };
      }

      return { count, windowStart };
    } catch {
      return { count: 0, windowStart: Date.now() };
    }
  }, [guestSearchKey, isSignedIn]);

  const persistGuestSearchMeta = useCallback(
    (meta) => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(guestSearchKey, JSON.stringify(meta));
      } catch {
        // ignore persistence errors
      }
    },
    [guestSearchKey]
  );

  const canGuestSearch = useCallback(() => {
    if (isSignedIn) return true;
    const meta = getGuestSearchMeta();
    return meta.count < GUEST_SEARCH_LIMIT;
  }, [getGuestSearchMeta, isSignedIn]);

  const trackGuestSearch = useCallback(() => {
    if (isSignedIn) return true;
    if (DISABLE_GUEST_SEARCH_LIMIT) return true;
    const now = Date.now();
    let meta = getGuestSearchMeta();

    // Reset window if expired
    if (now - meta.windowStart > GUEST_SEARCH_WINDOW_MS) {
      meta = { count: 0, windowStart: now };
    }

    if (meta.count >= GUEST_SEARCH_LIMIT) {
      promptSignUp();
      return false;
    }

    const nextMeta = { count: meta.count + 1, windowStart: meta.windowStart };
    persistGuestSearchMeta(nextMeta);
    return true;
  }, [GUEST_SEARCH_LIMIT, GUEST_SEARCH_WINDOW_MS, getGuestSearchMeta, isSignedIn, persistGuestSearchMeta, promptSignUp]);

  const handleSearch = async () => {
    const query = address.trim();
    if (!query || isLoading || curatedView) return;

    if (!trackGuestSearch()) return;

    onSearch({ query });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const fetchZipFromCoords = async (latitude, longitude) => {
    // 1) US Census geocoder (US-only, no key)
    try {
      const censusUrl = `https://geocoding.geo.census.gov/geocoder/geographies/coordinates?benchmark=2020&vintage=Current&format=json&y=${latitude}&x=${longitude}`;
      const censusRes = await fetch(censusUrl);
      if (censusRes.ok) {
        const data = await censusRes.json();
        const zip =
          data?.result?.geographies?.["Census ZIP Code Tabulation Areas"]?.[0]
            ?.ZCTA5 ||
          data?.result?.geographies?.Counties?.[0]?.GEOID?.slice(0, 5) ||
          null;
        if (zip) return { query: zip, zipFound: true };
      }
    } catch (_) {
      // ignore and fall through
    }

    // 2) OpenCage (preferred global, requires API key)
    if (opencageKey) {
      try {
        const ocUrl = `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${opencageKey}&no_annotations=1&language=en&limit=1`;
        const ocRes = await fetch(ocUrl);
        if (ocRes.ok) {
          const data = await ocRes.json();
          const components = data?.results?.[0]?.components || {};
          const postcode = components.postcode || components.postal_code || null;
          if (postcode) {
            return { query: postcode, zipFound: true };
          }
          // if city/state available and country is US, try zippopotam
          const country = (components.country_code || "").toLowerCase();
          if (country === "us") {
            const city =
              components.city ||
              components.town ||
              components.village ||
              components.hamlet ||
              components.locality ||
              null;
            const stateCode =
              (components["ISO3166-2-lvl4"] &&
                components["ISO3166-2-lvl4"].split("-").pop()) ||
              (components.state_code && components.state_code.length === 2
                ? components.state_code
                : null) ||
              null;
            if (city && stateCode) {
              const zUrl = `https://api.zippopotam.us/us/${encodeURIComponent(
                stateCode
              )}/${encodeURIComponent(city)}`;
              const zRes = await fetch(zUrl);
              if (zRes.ok) {
                const zData = await zRes.json();
                const zPost = zData?.places?.[0]?.["post code"];
                if (zPost) {
                  const zipMatch = zPost.toString().match(/\b\d{5}(?:-\d{4})?\b/);
                  if (zipMatch) {
                    return {
                      query: zipMatch[0].slice(0, 5),
                      zipFound: true,
                    };
                  }
                }
              }
            }
          }
        }
      } catch (_) {
        // ignore and fall through
      }
    }

    // 3) Nominatim (OpenStreetMap) as fallback (keyless; may miss some postcodes)
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;
      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          "User-Agent": "snapgrad-app/1.0 (localhost testing)",
        },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const addr = data?.address || {};
      const rawZip = addr.postcode || data?.postcode || null;
      if (rawZip) {
        return { query: rawZip, zipFound: true };
      }

      // fallback to place string so we still search something
      const place =
        addr.city ||
        addr.town ||
        addr.village ||
        addr.hamlet ||
        addr.locality ||
        addr.county ||
        addr.state ||
        addr.country;
      if (place) {
        return { query: place, zipFound: false };
      }
    } catch (_) {
      // ignore
    }

    return null;
  };

  const handleUseLocation = useCallback(() => {
    if (locating || isLoading || curatedView) return;
    if (!canGuestSearch()) {
      promptSignUp();
      return;
    }

    if (!navigator.geolocation) return;

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords || {};
          if (latitude == null || longitude == null) return;

          const geoResult = await fetchZipFromCoords(latitude, longitude);
          if (!geoResult?.query) return;

          if (!trackGuestSearch()) return;
          setAddress(geoResult.query);
          // Auto-trigger search once when geolocation succeeds
          onSearch({
            query: geoResult.query,
            coordinates: { latitude, longitude },
            zipFromGeolocation: geoResult.zipFound ? geoResult.query : null,
            radiusMiles: 35,
          });
        } catch (_) {
          // ignore errors to keep UX quiet
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 30000 }
    );
  }, [
    curatedView,
    isLoading,
    locating,
    onSearch,
    canGuestSearch,
    promptSignUp,
    trackGuestSearch,
  ]);

  useEffect(() => {
    if (autoLocationPromptedRef.current) return;
    if (curatedView || isLoading || isSearched) return;
    if (!navigator.geolocation) return;
    if (window.location.hostname !== "snapgrad.snaphomz.com") return;
    if (!canGuestSearch()) return;

    autoLocationPromptedRef.current = true;
    handleUseLocation();
  }, [
    canGuestSearch,
    curatedView,
    handleUseLocation,
    isLoading,
    isSearched,
  ]);

  const viewTitles = {
    overview: !isSearched
      ? "Overview"
      : lastQuery
      ? `Results Near ${displayQuery}`
      : "College Readiness",
    stem: "Best For STEM",
    athletes: "Best For Athletes",
  };

  const viewDescriptions = {
    overview:
      "Enter an address, city, or zip code to discover schools with the best fit for your next step toward college.",
    stem: "Discover top STEM-focused high schools curated by our data team.",
    athletes: "See high schools that excel in athletics, facilities, and sports culture.",
  };

  const searchedLabel = viewTitles[activeView] ?? "Overview";
  const descriptionCopy = viewDescriptions[activeView] ?? viewDescriptions.overview;

  return (
    <div className="mx-auto px-4 sm:px-8 lg:px-[133px] py-8 lg:py-16">
      <div className="bg-white rounded-[32px] p-6 sm:p-10 lg:p-[52px] shadow-sm border border-[#EEE5DF]">
        <div className="space-y-10">
          {/* Header Text */}
          <div className="space-y-2 text-center">
            <h2 className="font-unbounded text-[24px] sm:text-[28px] lg:text-[32px] font-semibold text-black">
              {searchedLabel}
            </h2>
            <p className="font-satoshi text-[18px] text-[#575757] max-w-2xl mx-auto">
              {descriptionCopy}
            </p>
          </div>

          {!curatedView && (
            <>
              {/* LOCATION INPUT – Centered + Smaller */}
              <form
                className="flex justify-center"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSearch();
                }}
              >
                <div className="w-full max-w-[650px] space-y-3">
                  <label className="font-satoshi text-[20px] font-medium text-black">
                    Location
                  </label>

                  <div className="h-[70px] rounded-[18px] border border-[#F0F0F0] bg-[#F2F2F2] px-5 flex items-center">
                    <MapPin className="w-5 h-5 text-[#575757]" />
                    <input
                      type="text"
                      value={address}
                      onChange={(event) => setAddress(event.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Address, city/state, or zip"
                      disabled={isLoading}
                      className="w-full bg-transparent font-satoshi text-[18px] text-[#575757] placeholder:text-[#A1A1A1] outline-none"
                    />
                  </div>
                </div>
              </form>

              {/* Centered Search Button */}
              <div className="flex flex-col items-center gap-3 mt-4">
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isLoading || !address.trim()}
                    className="inline-flex items-center justify-center gap-3 px-10 py-[14px] rounded-[31px] bg-[#F07639] 
                               font-unbounded text-[17px] font-semibold text-white 
                               hover:bg-[#E8560C] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-5 h-5" />
                        Search Schools
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleUseLocation}
                    disabled={isLoading || locating}
                    className="inline-flex items-center justify-center gap-2 px-6 py-[14px] rounded-[31px] border border-[#EEE5DF] bg-white text-[#575757] font-unbounded text-[15px] hover:border-[#F07639] hover:text-[#F07639] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {locating ? (
                      <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CurrentLocationIcon className="w-4 h-4 text-[#F07639]" />
                    )}
                    Use current location
                  </button>
                </div>

                {/* Guest search usage helper */}
                {!isSignedIn && (
                  <div className="mt-2 text-center font-satoshi text-[13px] text-[#777]">
                    {(() => {
                      const meta = getGuestSearchMeta();
                      const used = Math.min(meta.count, GUEST_SEARCH_LIMIT);
                      return `You have used ${used} of ${GUEST_SEARCH_LIMIT} free searches in the last 24 hours.`;
                    })()}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Query Tag (Same as before) */}
          {!curatedView && isSearched && lastQuery && (
            <div className="flex justify-center items-center gap-3 text-[#575757] font-satoshi text-[16px] mt-2">
              <MapPin className="w-4 h-4" />
              Showing results for:
              <span className="font-semibold text-black">{displayQuery}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchSection;
